import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  RealStateInvestmentRow,
  realStatePaidLabel,
  realStatePaidVariant,
  realStateUsageLabel,
  roundMoney,
} from '../../models/real-state-investment.model';
import { SummaryRealStateInvestmentListModel } from '../../models/summary-real-state-investment.model';
import { RealStateInvestmentEntityService } from '../../services/real-state-investment-entity.service';
import { createRealStateInvestmentTableConfig, TableRowAction } from './real-state-investment-table.config';
import { RealStateInvestmentFormDialogComponent } from '../real-state-investment-form-dialog/real-state-investment-form-dialog.component';

import { InvestmentPropertyService } from '../../../../../modules/investment/property/investment-property.service';
import { InvestmentPropertyDialogPayComponent } from '../../../../../modules/investment/property/investment-property-dialog-pay.component';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';
import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { investmentListPayload, investmentSearchFromState } from '../../../shared/investment-list.util';

@Component({
  standalone: false,
  selector: 'app-real-state-investment-list',
  templateUrl: './real-state-investment-list.components.html',
  styleUrls: ['./real-state-investment-list.components.css']
})
export class RealStateInvestmentListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('selectionSummary') selectionSummary!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'home';
  title = 'Inversiones inmobiliarias';
  subTitle = 'Consulta, registra y paga tus inversiones en inmuebles';

  quickSearch = '';
  totals = { count: 0, total: 0, paid: 0, pending: 0 };
  selectedUids: string[] = [];
  detailVersion = 0;

  private rows: RealStateInvestmentRow[] = [];
  private selectedRows: Record<string, RealStateInvestmentRow> = {};
  private summary: SummaryRealStateInvestmentListModel | null = null;
  private totalItems = 0;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private entity: RealStateInvestmentEntityService,
    private propertyService: InvestmentPropertyService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private loading: LoadingDialogService,
    private cdr: ChangeDetectorRef,
  ) { }

  get hasSelection(): boolean {
    return this.selectedUids.length > 0;
  }

  ngOnInit(): void {
    const money = (val: number) => Utils.FormatMoney(val ?? 0) + ' €';
    const date = (val: any) => this.formatDateValue(val);

    this.tableConfig = createRealStateInvestmentTableConfig({
      actionMap: {
        [TableRowAction.Edit]: (row) => this.edit(row),
        [TableRowAction.Delete]: (row) => this.delete(row),
        [TableRowAction.DownloadImage]: (row) => this.downloadFileImage(row),
        [TableRowAction.DownloadPdf]: (row) => this.downloadFilePdf(row),
        [TableRowAction.Pay]: (row) => this.pay(row),
        [TableRowAction.Contabilizar]: () => this.addManualAccountMovements(),
      },
      selectable: true,
      columns: [
        { key: 'contactsName', label: 'Proveedor', sortable: true, sortKey: 'ContactsName', ellipsis: true, onCellClick: (row: RealStateInvestmentRow) => this.edit(row) },
        { key: 'investmentDescription', label: 'Descripción', sortable: true, sortKey: 'InvestmentDescription', ellipsis: true },
        { key: 'accountingModelsNumber', label: 'Cuenta', sortable: true, sortKey: 'AccountingModelsNumber' },
        { key: 'investmentDate', label: 'Fecha de compra', sortable: true, sortKey: 'InvestmentDate', formatFn: date },
        { key: 'cadastralReference', label: 'Ref. catastral', sortable: true, sortKey: 'CadastralReference', hidden: true },
        { key: 'usage', label: 'Uso', sortable: false, formatFn: (val: number) => realStateUsageLabel(val), hidden: true },
        { key: 'base', label: 'Total sin impuestos', sortable: true, sortKey: 'Base', formatFn: money, hidden: true },
        { key: 'taxAmount', label: 'Impuestos', sortable: true, sortKey: 'TaxAmount', formatFn: money, hidden: true },
        { key: 'total', label: 'Total', sortable: true, sortKey: 'Total', formatFn: money },
        { key: 'totalPaid', label: 'Pagado', sortable: true, sortKey: 'TotalPaid', formatFn: money },
        { key: 'pending', label: 'Pendiente', sortable: true, sortKey: 'Pending', formatFn: (val: number) => money(Math.max(Number(val) || 0, 0)) },
        {
          key: 'paymentStatus', label: 'Pago', sortable: true, sortKey: 'PaymentStatus',
          badge: {
            variantFn: (_: any, row: RealStateInvestmentRow) => realStatePaidVariant(row),
            labelFn: (_: any, row: RealStateInvestmentRow) => realStatePaidLabel(row),
          },
        },
        {
          key: 'companyPremisesOrOffice', label: 'Local/Oficina', sortable: true, sortKey: 'CompanyPremisesOrOffice',
          badge: {
            variantFn: (val: boolean) => val ? 'info' : 'neutral',
            labelFn: (val: boolean) => val ? 'Sí' : 'No',
          },
        },
      ],
      headerActions: [
        { label: '', icon: 'add', type: 'button', callback: () => this.create() },
      ],
      gridActions: [
        { label: 'Contabilizar (asiento libre)', icon: 'show_chart', type: 'button', callback: () => this.addManualAccountMovements() },
      ],
      rowConfig: {
        onToggleSelect: (row: RealStateInvestmentRow, selected: boolean) => this.toggleSelection(row, selected),
      },
    });

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.selectionSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = investmentSearchFromState(state);
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  reload(): void {
    this.load();
  }

  create(): void {
    RealStateInvestmentFormDialogComponent.open(this.dialog, {})
      .subscribe(saved => { if (saved) this.load(); });
  }

  edit(row: RealStateInvestmentRow): void {
    if (!row || !row.uid) return;
    RealStateInvestmentFormDialogComponent.open(this.dialog, { uid: row.uid })
      .subscribe(saved => { if (saved) this.load(); });
  }

  pay(row: RealStateInvestmentRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    InvestmentPropertyDialogPayComponent.open(this.dialog, { investment: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(row: RealStateInvestmentRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar inversión',
      subtitle: [row.investmentDescription, row.contactsName].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminarán la inversión, sus pagos y los asientos contables asociados. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.rowDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando inversión…',
      errorText: 'Ha ocurrido un error al eliminar la inversión',
      action: () => this.propertyService.delete(row.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Inversión eliminada correctamente');
      this.unselect(row.uid);
      this.load();
    });
  }

  downloadFileImage(row: RealStateInvestmentRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.propertyService.downloadFileImage(row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetFileName('Imagen_Adjunta_Inversion', consts.EXT_PNG)))
      .catch(() => this.notify.error('No se pudo descargar la imagen'));
  }

  downloadFilePdf(row: RealStateInvestmentRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.propertyService.downloadFilePDF(row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetPdfName('Pdf_Adjunta_Inversion')))
      .catch(() => this.notify.error('No se pudo descargar el PDF'));
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') this.load();
    });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);
    this.entity.list(investmentListPayload(this.lastState))
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(item => item.__expanded).map(item => item.uid));
        this.rows = result.items.filter(item => !!item && !!item.uid).map(item => {
          const selected = this.selectedUids.indexOf(item.uid) !== -1;
          const row: RealStateInvestmentRow = { ...item, __expanded: expanded.has(item.uid), __selected: selected };
          if (selected) this.selectedRows[item.uid] = row;
          return row;
        });
        this.summary = result.summary;
        this.totalItems = result.total;
        this.detailVersion++;
        this.recomputeTotals();
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de inversiones inmobiliarias');
      });
  }

  private toggleSelection(row: RealStateInvestmentRow, selected: boolean): void {
    if (!row || !row.uid) return;
    if (selected) {
      if (this.selectedUids.indexOf(row.uid) === -1) this.selectedUids.push(row.uid);
      this.selectedRows[row.uid] = row;
    } else {
      this.unselect(row.uid);
    }
    this.recomputeTotals();
    this.cdr.markForCheck();
  }

  private unselect(uid: string): void {
    this.selectedUids = this.selectedUids.filter(item => item !== uid);
    delete this.selectedRows[uid];
  }

  private recomputeTotals(): void {
    if (!this.hasSelection) {
      const summary = this.summary;
      this.totals = {
        count: this.totalItems,
        total: roundMoney(summary ? summary.total : 0),
        paid: roundMoney(summary ? summary.totalPaid : 0),
        pending: roundMoney(summary ? summary.pending : 0),
      };
      return;
    }
    const source = this.selectedUids.map(uid => this.selectedRows[uid]).filter(row => !!row);
    this.totals = {
      count: source.length,
      total: roundMoney(source.reduce((acc, row) => acc + (Number(row.total) || 0), 0)),
      paid: roundMoney(source.reduce((acc, row) => acc + (Number(row.totalPaid) || 0), 0)),
      pending: roundMoney(source.reduce((acc, row) => acc + Math.max(Number(row.pending) || 0, 0), 0)),
    };
  }

  private rowDetails(row: RealStateInvestmentRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.accountingModelsNumber) details.push({ label: 'Cuenta', value: row.accountingModelsNumber });
    const date = this.formatDateValue(row.investmentDate);
    if (date) details.push({ label: 'Fecha de compra', value: date });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.total ?? 0) + ' €' });
    details.push({ label: 'Pagado', value: Utils.FormatMoney(row.totalPaid ?? 0) + ' €' });
    return details;
  }

  private formatDateValue(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
    return formatDate(date, 'dd/MM/yyyy', 'es-ES');
  }
}
